// Priority SEO page: Breast augmentation in Vietnam.
const breastAugmentation = {
  slug: "breast-augmentation-vietnam",
  metaTitle: "Breast Augmentation in Vietnam | Patient Guide",
  metaDescription:
    "Learn about breast augmentation in Vietnam, including implants, surgeon and hospital selection, risks, recovery, travel and cost factors.",
  eyebrow: "Breast surgery",
  h1: "Breast Augmentation in Vietnam: Implants, Safety and Recovery",
  intro:
    "Breast augmentation increases or reshapes the breasts, most often using implants. The right approach depends on your anatomy, goals and health, and can only be confirmed by a surgeon after an in-person examination.",
  summary:
    "Breast augmentation may use implants or, in some cases, fat transfer. Implant type, size and placement are clinical decisions made with your surgeon. Understand the risks — including capsular contracture and the fact that implants are not lifetime devices — and plan recovery and travel around your surgeon's advice. Results vary and are never guaranteed.",
  published: "December 2025",
  updated: "December 2025",
  breadcrumb: [{ name: "Procedures", to: "/procedures" }, { name: "Breast Surgery", to: "/breast-surgery-vietnam" }, { name: "Breast Augmentation in Vietnam", to: "/breast-augmentation-vietnam" }],
  sections: [
    {
      id: "involves",
      h2: "What breast augmentation involves",
      paragraphs: [
        "Breast augmentation typically places an implant to increase volume or improve shape and symmetry. In some cases fat transfer may be considered instead of, or alongside, implants.",
      ],
    },
    {
      id: "implant-vs-fat",
      h2: "Implant versus fat-transfer overview",
      paragraphs: [
        "Implants provide a more predictable increase in size, while fat transfer offers a smaller, more natural change and depends on available donor fat and graft survival. A surgeon can explain which — if either — may suit you.",
      ],
    },
    {
      id: "implant-choices",
      h2: "Implant choices",
      paragraphs: ["Several implant characteristics are considered together. These are clinical choices, not simply preferences."],
      subsections: [
        { h3: "Shape", paragraphs: ["Round or teardrop (anatomical) implants create different profiles."] },
        { h3: "Profile", paragraphs: ["Profile describes how far the implant projects, affecting the final look."] },
        { h3: "Placement", paragraphs: ["Implants may be placed above or below the chest muscle, each with trade-offs."] },
        { h3: "Surface and product", paragraphs: ["Implant surface and the specific product are relevant to safety and follow-up. Ask for documentation of the exact device used."] },
        { h3: "Choosing size", paragraphs: ["Size is chosen with your surgeon based on your anatomy and goals, not size alone."] },
      ],
    },
    {
      id: "with-lift",
      h2: "Breast lift combined with augmentation",
      paragraphs: [
        "If there is significant sagging, augmentation alone may not achieve the desired shape, and a breast lift may be discussed in combination. Whether this is appropriate is assessed individually.",
      ],
    },
    {
      id: "candidacy",
      h2: "Who may be assessed and medical history considerations",
      paragraphs: [
        "Suitability depends on your health, anatomy and expectations. Share your full medical history, including any family history of breast disease, previous breast surgery and medication, so risks can be assessed.",
      ],
    },
    {
      id: "surgeon-hospital",
      h2: "Surgeon and hospital selection",
      paragraphs: [
        "Ask about the surgeon's experience with breast augmentation, the implant products they use and why, and confirm surgery takes place in a licensed hospital or appropriate facility with a qualified anaesthetist.",
      ],
    },
    {
      id: "risks",
      h2: "Risks and complications",
      paragraphs: ["As with any surgery, breast augmentation carries risks that should be discussed in detail."],
      subsections: [
        { h3: "Capsular contracture", paragraphs: ["Firm scar tissue can form around an implant and may sometimes require further surgery."] },
        { h3: "Implant rupture", paragraphs: ["Implants can fail over time and may need replacement."] },
        { h3: "Infection", paragraphs: ["Infection is a recognised risk after any implant surgery."] },
        { h3: "Changes over time", paragraphs: ["Breasts continue to change with age, weight and pregnancy, which can affect the long-term result."] },
      ],
      callout: "Breast implants are not considered lifetime devices. Further surgery may be needed in the future to replace or remove them.",
    },
    {
      id: "recovery",
      h2: "Recovery timeline and support garments",
      paragraphs: [
        "Expect soreness and swelling in the first weeks, with activity gradually resumed on your surgeon's advice. A support garment is usually recommended, and heavy activity is avoided for a period after surgery.",
      ],
    },
    {
      id: "stay",
      h2: "How long to remain in Vietnam and flying after surgery",
      paragraphs: [
        "Plan your stay around follow-up appointments. Flying should only take place once your surgeon gives travel clearance, as long flights can increase certain risks after surgery.",
      ],
    },
    {
      id: "documentation",
      h2: "Implant documentation and revision considerations",
      paragraphs: [
        "Keep documentation of your exact implant, as this supports future follow-up, replacement or revision. If you already have implants and are seeking changes, this is considered a revision case and is assessed differently.",
      ],
    },
    {
      id: "cost",
      h2: "Factors affecting cost",
      priceTable: ["Surgeon fee", "Facility / theatre fee", "Anaesthesia", "Implants / devices", "Pre-operative tests", "Support garments and medication", "Follow-up appointments"],
    },
  ],
  faqs: [
    { q: "Are breast implants permanent?", a: "No. Implants are not considered lifetime devices and may need replacement or removal in the future. Regular review is recommended." },
    { q: "Which implant is best for me?", a: "Implant shape, profile, placement and size are clinical choices made with your surgeon based on your anatomy and goals, not a single \"best\" option." },
    { q: "Is a particular implant FDA-approved?", a: "We do not describe any implant as approved unless the exact product and approval are verified. Ask your surgeon for documentation of the specific device." },
    { q: "How long before I can fly home?", a: "Only once your surgeon gives travel clearance. Flying too soon can increase certain risks after surgery." },
  ],
  sources: [
    { label: "International Society of Aesthetic Plastic Surgery (ISAPS)", url: "https://www.isaps.org/" },
    { label: "NHS — Breast enlargement (implants)", url: "https://www.nhs.uk/conditions/cosmetic-procedures/breast-enlargement/" },
  ],
  relatedTitle: "Related procedures & guides",
  related: [
    { to: "/breast-surgery-vietnam", title: "Breast surgery in Vietnam", desc: "All breast procedures" },
    { to: "/revision-plastic-surgery-vietnam", title: "Revision plastic surgery", desc: "Implant and result concerns" },
    { to: "/plastic-surgery-safety-vietnam", title: "Is plastic surgery in Vietnam safe?", desc: "How safety is determined" },
    { to: "/how-to-choose-a-plastic-surgeon-in-vietnam", title: "How to choose a surgeon", desc: "Licensing and experience" },
  ],
};

export default breastAugmentation;
